/**
 * The reporter's IANA timezone, for the ETA in the acknowledgement email
 * (ENG-2936, part of ENG-2649).
 *
 * The PRD states the customer-facing ETA as an **absolute local time** and rules
 * out "in 4 hours" explicitly, so a timezone is a required input to Email 1 —
 * not a nicety, and not something to infer downstream from an IP address.
 *
 * ## Why the browser rather than the user profile
 *
 * Profile timezone is optional at signup, so it is populated for some people and
 * not others. That produces a silent mix of correct and defaulted times with
 * nothing on the record to tell them apart — the worst shape for a field a
 * customer reads. `Intl` answers for everyone, every time, and reports where the
 * reporter actually is rather than where they once said they were.
 *
 * Returns `undefined` rather than guessing when the environment cannot say. The
 * server falls back to the tenant default, which is a decision made in one place
 * with the tenant in hand, instead of a `UTC` invented here.
 */
export function resolveReporterTimezone(): string | undefined {
  try {
    // Older engines and locked-down environments can return an empty string
    // here, which is not a usable zone — treat it as absent.
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    // Never let diagnostics collection break the report itself. A bug report
    // that fails to send because of the timezone lookup is strictly worse than
    // one that arrives without it.
    return undefined;
  }
}
